class AnalyticsService {
  constructor() {
    this.enabled = process.env.REACT_APP_ENABLE_ANALYTICS === 'true';
    this.scriptLoaded = false;
    this.gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    this.initPromise = null;
    this.isInitialized = false;
  }

  init() {
    if (!this.enabled || !this.gaId) {
      return Promise.resolve();
    }

    if (this.isInitialized) {
      return Promise.resolve();
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      try {
        const existingScript = document.getElementById('ga-script');
        if (existingScript) {
          existingScript.remove();
          this.scriptLoaded = false;
        }

        const script = document.createElement('script');
        script.id = 'ga-script';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
        
        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          window.gtag = function() { 
            window.dataLayer.push(arguments); 
          }
          window.gtag('js', new Date());
          window.gtag('config', this.gaId);
          
          this.scriptLoaded = true;
          this.isInitialized = true;
          this.initPromise = null;
          resolve();
        };

        script.onerror = (error) => {
          this.scriptLoaded = false;
          this.isInitialized = false;
          this.initPromise = null;
          reject(error);
        };

        document.head.appendChild(script);
      } catch (error) {
        this.scriptLoaded = false;
        this.isInitialized = false;
        this.initPromise = null;
        reject(error);
      }
    });

    return this.initPromise;
  }

  cleanup() {
    try {
      // Rimuovi tutti gli script di GA
      const scripts = [...document.querySelectorAll('script[src*="googletagmanager.com"], script[id="ga-script"]')];
      scripts.forEach(script => {
        try {
          script.parentNode.removeChild(script);
        } catch (e) {
          // Se si verifica un errore, prova un altro metodo
          script.remove();
        }
      });
      
      // Rimuovi anche gli iframe che Google Analytics potrebbe aver creato
      const iframes = [...document.querySelectorAll('iframe[src*="google"]')];
      iframes.forEach(iframe => {
        try {
          iframe.parentNode.removeChild(iframe);
        } catch (e) {
          iframe.remove();
        }
      });
  
      // Resetta le variabili globali
      if (window.dataLayer) {
        delete window.dataLayer;
      }
      if (window.gtag) {
        delete window.gtag;
      }
      if (window.ga) {
        delete window.ga;
      }
      if (window.google_tag_manager) {
        delete window.google_tag_manager;
      }
      
      // Rimuovi tutti i cookie associati a GA
      this.removeCookie('_ga');
      this.removeCookie('_gid');
      this.removeCookie('_gat');
      
      // Cookie specifico per il measurement ID (esempio: _ga_XXXXXXXX)
      if (this.gaId) {
        const idPart = this.gaId.replace('G-', '');
        this.removeCookie(`_ga_${idPart}`);
      }
      
      // Reset dei flag
      this.scriptLoaded = false;
      this.isInitialized = false;
      this.initPromise = null;
    } catch (error) {
      console.error('Errore durante la pulizia di GA:', error);
    }
  }

  pageView(path) {
    if (!this.enabled || !this.isInitialized || !window.gtag) {
      return;
    }
    
    try {
      window.gtag('config', this.gaId, { page_path: path });
    } catch (error) {
      console.error('Errore durante il tracciamento della pagina:', error);
      // In caso di errore, resetta lo stato
      this.cleanup();
    }
  }

  removeCookie(name) {
    try {
      const domains = [
        window.location.hostname,
        '.' + window.location.hostname,
        window.location.hostname.split('.').slice(1).join('.'),
        '.' + window.location.hostname.split('.').slice(1).join('.')
      ];
  
      const paths = ['/', '/walky', ''];
  
      domains.forEach(domain => {
        paths.forEach(path => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
        });
        // Prova anche senza specificare il path
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}`;
      });
      
      // Fallback per rimuovere il cookie senza domain e path
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    } catch (error) {
      console.error('Errore durante la rimozione del cookie:', error);
    }
  }

  event(category, action, label = null, value = null) {
    if (!this.enabled || !this.isInitialized || !window.gtag) {
      return;
    }

    try {
      const eventParams = {
        event_category: category,
        event_label: label,
        value: value
      };
  
      window.gtag('event', action, eventParams);
    } catch (error) {
      console.error('Errore durante l\'invio dell\'evento:', error);
      // In caso di errore, resetta lo stato
      this.cleanup();
    }
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService; 